import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import useGetUserRole from '~/hooks/useGetUserRole';
import { IS_AUTHENTICATED_KEY, REFRESH_BEFORE_MS } from '~/modules/auth/constants';
import { getTokenExpiry, resolveSignInPathByRoute } from '~/modules/auth/utils';
import { refreshAccessToken } from '~/modules/auth/utils';
import { selectAccessToken } from '~/store/covenants/selectors';
import { AppDispatch } from '~/store/store.ts';

export const useRefreshToken = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector(selectAccessToken);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAuthorized = localStorage.getItem(IS_AUTHENTICATED_KEY);
  const { isAssetManager } = useGetUserRole();
  const isAssetManagerPage = location.pathname.includes('asset-manager');

  const shouldManageRefresh = Boolean(token) || Boolean(isAuthorized);

  const redirectPath = resolveSignInPathByRoute(location.pathname);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearTimer();
    if (!shouldManageRefresh) return;

    let isCancelled = false;

    const handleUnauthorizedRedirect = () => {
      if (isCancelled) return;
      const currentPath = `${location.pathname}${location.search}`;
      navigate(redirectPath, { state: { from: currentPath }, replace: true });
    };

    const scheduleNextRefresh = (accessToken: string) => {
      if (isCancelled || isAssetManager) return;

      const now = Date.now();
      const expiresAt = getTokenExpiry(accessToken || '') ?? 0;
      const delay = Math.max(expiresAt - REFRESH_BEFORE_MS - now, 0);

      clearTimer();
      timerRef.current = setTimeout(() => {
        clearTimer();
        runRefresh();
      }, delay);
    };

    const runRefresh = async () => {
      try {
        const newToken = await refreshAccessToken(dispatch);
        if (isCancelled) return;
        scheduleNextRefresh(newToken || '');
      } catch {
        handleUnauthorizedRedirect();
      }
    };

    if (token) {
      scheduleNextRefresh(token);
    } else {
      !isAssetManagerPage && runRefresh();
    }

    if (!isAssetManagerPage && isAssetManager) {
      runRefresh();
    }
    return () => {
      clearTimer();
      isCancelled = true;
    };
  }, [
    dispatch,
    location.pathname,
    location.search,
    navigate,
    redirectPath,
    shouldManageRefresh,
    token,
    isAssetManager,
    isAssetManagerPage,
    clearTimer,
  ]);
};
